import React, { ReactNode } from 'react';
import {
  ResponsiveSizeValue,
  ResponsiveValue,
  SizeValue,
  getResponsiveValue,
  processStylingProps,
} from '../_internal/styling';
import SingleSelectBase, {
  ExtendableSingleSelectBaseProps,
} from '../SingleSelectBase';
import SelectableButton, { SelectableButtonProps } from '../SelectableButton';
import Box from '../Box';
import Stack from '../Stack';
import Inline from '../Inline';
import useScreenSize from '../_internal/useScreenSize';
import { MarginProps } from '../_internal/spacing';
import { getNode } from '../_internal/selectBase';
import { MinOptionInfo } from '../_internal/selectBase/types';
import { OptionContainerProps, OptionInfo } from './types';

import styles from './singleSelectButtonGroup.module.scss';
import logger from '../_internal/logger';

interface SquareDirectionProps {
  /**
   * Style of selectable option buttons.
   *
   * @default 'square'
   */
  variant?: 'square';
  /**
   * The orientation of the selectable option buttons. This value is responsive and may be configured for multiple screen sizes.
   *
   * @default 'horizontal'
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * The spacing between the selectable option buttons in the `direction`. `"grouped"` is a special value to create a horizontal square button group. It's the default when the `variant` is `"square"` and the `direction` is `"horizontal"`. Otherwise the default is based on the chosen `variant` & `direction`. This value is responsive and may be configured for multiple screen sizes.
   */
  spacing?: ResponsiveValue<'grouped' | SizeValue>;
}
interface PillDirectionProps {
  variant?: 'pill';
  direction?: ResponsiveValue<'horizontal' | 'vertical'>;
  spacing?: ResponsiveSizeValue;
}
type VariantDirectionSpacingProps = SquareDirectionProps | PillDirectionProps;

interface CommonProps {
  /**
   * The color of the selectable option buttons.
   *
   * @default 'default'
   */
  color?: SelectableButtonProps['color'];
  /**
   * The minimum width of the selectable option buttons. A `true` value means use the default `min-width`, otherwise specific a value. Defaults to `false` for horizontal layout and `true` for vertical layout.
   */
  optionMinWidth?: SelectableButtonProps['minWidth'];
  /**
   * Whether or not one of the selectable option button has to be selected.
   *
   * @default false
   */
  required?: boolean;
  /**
   * The size of the selectable option buttons.
   *
   * @deprecated This value is no longer used, and can be removed.
   */
  size?: SelectableButtonProps['size'];
}

type Props<
  ValueString extends string,
  FullOptionInfo extends MinOptionInfo<ValueString>,
> = CommonProps &
  OptionContainerProps<ValueString, FullOptionInfo> &
  VariantDirectionSpacingProps &
  MarginProps;

export type SingleSelectButtonGroupProps<
  ValueString extends string,
  FullOptionInfo extends OptionInfo<ValueString>,
> = Omit<
  ExtendableSingleSelectBaseProps<
    ValueString,
    FullOptionInfo,
    Props<ValueString, FullOptionInfo>
  >,
  'children'
>;

const getContainer = (
  variant: 'square' | 'pill',
  direction: 'horizontal' | 'vertical',
  spacing?: ResponsiveValue<'grouped' | SizeValue>,
  margin?: MarginProps,
): ((children: ReactNode) => ReactNode) => {
  // TODO: If (or when) we need to pass props to these containers
  // (tagName, className, aria-*, etc). We can add a `containerProps` prop
  // that we can pass through to these containers.
  if (variant === 'square' && spacing === 'grouped') {
    return children => (
      <Box
        className={
          direction === 'horizontal'
            ? styles['grouped-horizontal']
            : styles['grouped-vertical']
        }
        data-testid="single-select-button-group-container"
        {...margin}
      >
        {children}
      </Box>
    );
  }

  if (direction === 'vertical') {
    return children => (
      <Stack
        spacing={spacing || 1}
        data-testid="single-select-button-group-container"
        {...margin}
      >
        {children}
      </Stack>
    );
  }

  return children => (
    <Inline
      spacing={spacing || 0.5}
      wrap
      data-testid="single-select-button-group-container"
      {...margin}
    >
      {children}
    </Inline>
  );
};

/**
 * A single-select button group can be used as a visual alternative to radio group, using selectable buttons instead of radio buttons. However, the functionality is the exact same: only one button can be selected at a time.
 */
const SingleSelectButtonGroup = <
  ValueString extends string,
  FullOptionInfo extends OptionInfo<ValueString>,
>({
  color = 'default',
  direction = 'horizontal',
  optionContainer,
  optionMinWidth = direction === 'vertical',
  required = false,
  size,
  spacing,
  variant = 'square',
  options,
  m,
  mt,
  mb,
  my,
  ml,
  mr,
  mx,
  ...singleSelectBaseProps
}: SingleSelectButtonGroupProps<ValueString, FullOptionInfo>) => {
  if (size !== undefined) {
    logger.warn('`size` is deprecated and can be removed.');
  }

  const screenSize = useScreenSize();
  const responsiveDirection =
    getResponsiveValue(screenSize, direction) || 'horizontal';
  const responsiveSpacing = getResponsiveValue(screenSize, spacing);

  const margin = { m, mt, mb, my, ml, mr, mx } as MarginProps;
  const container = getContainer(
    variant,
    responsiveDirection,
    responsiveSpacing,
    margin,
  );

  const deprecatedDisabledOption = options.find(
    ({ disabled }) => disabled !== undefined,
  );

  if (deprecatedDisabledOption) {
    const mappedStatus = deprecatedDisabledOption.disabled
      ? 'disabled'
      : 'enabled';

    logger.warn(
      `The "disabled" property is deprecated on options of SingleSelectButtonGroup. Use \`status: "${mappedStatus}"\` instead.`,
    );
  }

  return (
    <SingleSelectBase
      container={container}
      options={options}
      {...singleSelectBaseProps}
    >
      {(
        { isSelected, value, disabled, ...selectableProps },
        currentOption,
        index,
      ) => {
        const processedCurrentOption = processStylingProps(
          currentOption,
          'SingleSelectButtonGroup',
          {
            stylingProps: 'warn',
            dangerousStylingProps: 'rewrite',
          },
        );
        const status = disabled ? 'disabled' : currentOption.status;

        return (
          <SelectableButton
            {...selectableProps}
            aria-label={currentOption['aria-label']}
            color={color}
            container={
              optionContainer &&
              (containerProps =>
                optionContainer(containerProps, currentOption, index))
            }
            // Set style dangerously to avoid duplicative warnings about the
            // style prop being set from SelectableButton.
            // eslint-disable-next-line @stitch-fix/no-dangerous-mode-react-styling-props
            dangerouslySetStyle={processedCurrentOption.style}
            endIcon={getNode(currentOption.endIcon, isSelected)}
            isSelected={isSelected}
            minWidth={optionMinWidth}
            required={required}
            startIcon={getNode(currentOption.startIcon, isSelected)}
            status={status}
            value={value}
            variant={variant}
            isVerticalSelectButtonGroup={
              direction === 'vertical' && spacing === 'grouped'
            }
          >
            {getNode(currentOption.label, isSelected) || value}
          </SelectableButton>
        );
      }}
    </SingleSelectBase>
  );
};

export default SingleSelectButtonGroup;
